/**
 * @fileoverview Hook de UI Layer para gestión de filtros rápidos del inbox con acciones masivas
 * @module useQuickFiltersUI
 * @category Hook - UI Layer
 * @since 1.0.0
 * @version 3.1.0
 */

import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import useInboxFilterOptions from '../data/useInboxFilterOptions';
import STATUS_CHAT_MAP from '../../../utils/statusChatMap';
import { getCommonAllowedStateIds, isStateOptionDisabled } from '../../../utils/chatStateOptions';
import {
  changeChatMessagingState,
  changeCustomerAgent,
  setChatReadToggleState
} from '../../../../actions/inbox/conversationActions';
import IconMC from '../../../components/IconMC';
import {
  buildAssignAgentPayload,
  buildMessagingStatePayload,
  buildReadStatePayload
} from '../utils/bulkConversationActions';

const useQuickFiltersUI = (filter = {}, onFilterChange, selectedConversations, onClearSelectedConversations) => {
  const { t } = useTranslation('InboxMC');
  const dispatch = useDispatch();
  const currentRetailerUser = useSelector((state) => state.mainReducer.currentRetailerUser || {});
  const canAssignChatsToAgents = Boolean(currentRetailerUser?.can_assign_chats_to_agents);

  const { agentListOptions } = useInboxFilterOptions();

  const quickFilters = useMemo(() => filter.quickFilters || { activity: [], status: ['pending'] }, [filter.quickFilters]);

  const selectedActivityFilters = useMemo(() => quickFilters.activity || [], [quickFilters.activity]);

  const selectedStatusFilters = useMemo(() => {
    const { status } = quickFilters;
    return status?.length > 0 ? status : ['pending'];
  }, [quickFilters]);

  const handleMarkAsUnread = useCallback(() => {
    dispatch(setChatReadToggleState(buildReadStatePayload(selectedConversations), { unreadState: true }));
  }, [dispatch, selectedConversations]);

  const handleAssignAgent = useCallback((agentValue) => {
    if (!canAssignChatsToAgents && Number(agentValue) !== Number(currentRetailerUser?.id)) return;

    const { body, options } = buildAssignAgentPayload(selectedConversations, agentValue);
    if (body.length > 0) {
      dispatch(changeCustomerAgent(body, options));
    }
    // Se limpia sin esperar la respuesta: con el filtro "Sin asignar" los chats asignados salen de la
    // lista, y si siguieran seleccionados se reenviarían ocultos en la siguiente transferencia.
    onClearSelectedConversations?.();
  }, [canAssignChatsToAgents, currentRetailerUser?.id, dispatch, onClearSelectedConversations, selectedConversations]);

  const handleChangeMessagingState = useCallback(async (messagingStateOption) => {
    const { body, options } = buildMessagingStatePayload(selectedConversations, messagingStateOption);
    if (!body.length) {
      onClearSelectedConversations?.();
      return;
    }
    await dispatch(changeChatMessagingState(body, options));
    onClearSelectedConversations?.();
  }, [dispatch, onClearSelectedConversations, selectedConversations]);

  const selectedStatusValues = useMemo(
    () => (selectedConversations || []).map((conversation) => Number(conversation.statusChat)),
    [selectedConversations]
  );

  const availableMessagingStateOptions = useMemo(() => {
    if (!selectedStatusValues.length) return [];

    const commonAllowedStateIds = getCommonAllowedStateIds(selectedStatusValues);

    const messagingStateOptions = commonAllowedStateIds.map((stateId) => ({
      id: stateId,
      label: t(`header.state_${STATUS_CHAT_MAP[stateId]?.value}`),
      disabled: isStateOptionDisabled(selectedStatusValues, stateId)
    }));

    return messagingStateOptions.filter((option) => !option.disabled);
  }, [selectedStatusValues, t]);

  const activitySelectOptions = useMemo(() => {
    const messagingStateActions = availableMessagingStateOptions.map((stateOption) => ({
      id: `mark_as_state_${stateOption.id}`,
      label: t('filters.activity.mark_as_state', { state: stateOption.label }),
      onClick: () => handleChangeMessagingState(stateOption.id)
    }));

    const transferLabel = t('chat_list.context_menu.transfer');
    const assignBlockedNotice = t('header.assign_permission_blocked_notice', {
      defaultValue: 'No tienes permiso para asignar chats a otros agentes'
    });

    const transferOption = canAssignChatsToAgents
      ? {
        id: 'transfer',
        label: transferLabel,
        submenu: agentListOptions.map((option) => ({
          icon: 'dot-filled',
          iconClassName:
          option.active ? 'tw-text-green-400' : 'tw-text-gray-200',
          onClick: () => handleAssignAgent(option.id),
          ...option
        })),
        iconPosition: 'right',
        dropdownClassName: "!tw-left-0 !tw-right-auto tw-overflow-x-auto tw-max-h-80 !tw-w-64"
      }
      : {
        id: 'transfer',
        label: transferLabel,
        disabled: true,
        disabledTitle: assignBlockedNotice,
        Content: () => (
          <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0 tw-w-full">
            <span className="tw-inline-flex tw-flex-shrink-0 tw-text-gray-400">
              <IconMC name="lock-outline" size="sm" />
            </span>
            <span className="tw-min-w-0 tw-truncate">{transferLabel}</span>
          </div>
        )
      };

    const options = [
      transferOption,
      {
        id: 'mark_as_unread',
        label: t('chat_list.context_menu.mark_unread'),
        onClick: handleMarkAsUnread
      },
      ...messagingStateActions
    ];

    return options;
  }, [
    agentListOptions,
    canAssignChatsToAgents,
    handleMarkAsUnread,
    handleAssignAgent,
    availableMessagingStateOptions,
    handleChangeMessagingState,
    t
  ]);

  const showBulkAssignToSelf = useMemo(() => (
    !canAssignChatsToAgents
    && Array.isArray(selectedConversations)
    && selectedConversations.length > 0
    && selectedConversations.some(
      (conversation) => Number(conversation.assignedAgent?.id) !== Number(currentRetailerUser?.id)
    )
  ), [canAssignChatsToAgents, currentRetailerUser?.id, selectedConversations]);

  const handleBulkAssignToSelf = useCallback(() => {
    if (!currentRetailerUser?.id) return;
    handleAssignAgent(currentRetailerUser.id);
  }, [currentRetailerUser?.id, handleAssignAgent]);

  const activityOptions = useMemo(() => [
    {
      id: 'no_read',
      label: t('filters.activity.unread')
    },
    {
      id: 'no_answer',
      label: t('filters.activity.no_response')
    }
  ], [t]);

  const statusOptions = useMemo(() => [
    {
      id: 'all',
      label: t('filters.status.all')
    },
    {
      id: 'pending',
      label: t('filters.status.to_resolve')
    },
    {
      id: 'resolved',
      label: t('filters.status.resolved')
    }
  ], [t]);

  const handleActivityToggle = useCallback((optionId) => {
    const newActivityFilters = selectedActivityFilters.includes(optionId)
      ? selectedActivityFilters.filter((id) => id !== optionId)
      : [...selectedActivityFilters, optionId];
    const updatedQuickFilters = {
      activity: newActivityFilters,
      status: selectedStatusFilters,
      isSelectingConversations: false
    };
    onFilterChange?.('quickFilters', updatedQuickFilters);
  }, [selectedActivityFilters, selectedStatusFilters, onFilterChange]);

  const handleStatusToggle = useCallback((optionId) => {
    const updatedQuickFilters = {
      activity: selectedActivityFilters,
      status: [optionId],
      isSelectingConversations: false
    };

    onFilterChange?.('quickFilters', updatedQuickFilters);
  }, [selectedActivityFilters, onFilterChange]);

  const closeSelectedTitle = useMemo(() => t('search.close_filters'), [t]);

  return useMemo(() => ({
    activityOptions,
    activitySelectOptions,
    closeSelectedTitle,
    handleActivityToggle,
    handleStatusToggle,
    selectedActivityFilters,
    selectedStatusFilters,
    statusOptions,
    showBulkAssignToSelf,
    handleBulkAssignToSelf
  }), [
    activityOptions,
    activitySelectOptions,
    closeSelectedTitle,
    handleActivityToggle,
    handleStatusToggle,
    selectedActivityFilters,
    selectedStatusFilters,
    statusOptions,
    showBulkAssignToSelf,
    handleBulkAssignToSelf
  ]);
};

export default useQuickFiltersUI;
